import {
  type AssetManifest,
  isEditableProp,
  MOB_IDS,
  type MobId,
  MOBS,
  PROP_IDS,
  PROPS,
} from '@shefira/shared';
import type Phaser from 'phaser';

import { uiText } from '../ui/text.js';

/**
 * The world editor's palette (ADR 0022): every prop that can be set down,
 * every inhabitant and every monster, each shown by its own sprite, drawn
 * from the textures the game already loaded. A click arms it; the next
 * click on the map sets it down.
 */

export type PaletteEntry = { kind: 'prop'; id: string } | { kind: 'spawn'; id: MobId };

type Tab = 'props' | 'folk' | 'monsters';

const TABS: readonly { tab: Tab; label: string }[] = [
  { tab: 'props', label: 'Décors' },
  { tab: 'folk', label: 'Habitants' },
  { tab: 'monsters', label: 'Monstres' },
];

/** The thumbnail's box, in CSS pixels. */
const THUMB = 44;

export interface PaletteOptions {
  scene: Phaser.Scene;
  manifest: AssetManifest;
  onPick: (entry: PaletteEntry | undefined) => void;
}

export class Palette {
  readonly root: HTMLElement;
  private readonly grid: HTMLElement;
  private readonly tabs = new Map<Tab, HTMLButtonElement>();
  private readonly cells = new Map<string, HTMLButtonElement>();
  private tab: Tab = 'props';
  private armed?: PaletteEntry;

  constructor(private readonly options: PaletteOptions) {
    this.root = document.createElement('div');
    this.root.className = 'palette';
    const bar = document.createElement('div');
    bar.className = 'palette__tabs';
    bar.setAttribute('role', 'tablist');
    for (const { tab, label } of TABS) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'palette__tab';
      button.setAttribute('role', 'tab');
      button.textContent = uiText(label);
      button.addEventListener('click', () => {
        this.show(tab);
      });
      this.tabs.set(tab, button);
      bar.append(button);
    }
    this.grid = document.createElement('div');
    this.grid.className = 'palette__grid';
    this.root.append(bar, this.grid);
    this.show('props');
  }

  /** Which entry the editor has armed, to light its cell. */
  setArmed(entry: PaletteEntry | undefined): void {
    this.armed = entry;
    for (const [key, cell] of this.cells) {
      cell.setAttribute('aria-pressed', key === keyOf(entry) ? 'true' : 'false');
    }
  }

  private show(tab: Tab): void {
    this.tab = tab;
    for (const [each, button] of this.tabs) {
      button.setAttribute('aria-selected', each === tab ? 'true' : 'false');
    }
    this.cells.clear();
    this.grid.replaceChildren(...this.entries().map((entry) => this.cell(entry)));
    this.setArmed(this.armed);
  }

  private entries(): PaletteEntry[] {
    if (this.tab === 'props') {
      return PROP_IDS.filter(isEditableProp)
        .sort()
        .map((id) => ({ kind: 'prop', id }));
    }
    const peaceful = this.tab === 'folk';
    return MOB_IDS.filter((id) => MOBS[id].peaceful === peaceful).map((id) => ({
      kind: 'spawn',
      id,
    }));
  }

  private cell(entry: PaletteEntry): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'palette__cell';
    const name = entry.kind === 'prop' ? entry.id : MOBS[entry.id].name;
    button.title = uiText(name);
    button.setAttribute('aria-label', uiText(name));
    const picture = this.thumbnail(entry);
    if (picture !== undefined) button.append(picture);
    else button.textContent = name.slice(0, 3);
    button.addEventListener('click', () => {
      this.options.onPick(keyOf(entry) === keyOf(this.armed) ? undefined : entry);
    });
    this.cells.set(keyOf(entry), button);
    return button;
  }

  /** The entry's sprite, fitted in the thumbnail's box without smoothing. */
  private thumbnail(entry: PaletteEntry): HTMLCanvasElement | undefined {
    const { scene, manifest } = this.options;
    let atlas: string | undefined;
    let frameName: string | undefined;
    if (entry.kind === 'prop') {
      const sprite = manifest.sprites[PROPS[entry.id]?.sprite ?? ''];
      atlas = sprite?.atlas;
      frameName = sprite?.frame;
    } else {
      const sheet = manifest.sheets[MOBS[entry.id].bodyKey];
      atlas = sheet?.atlas;
      frameName = sheet === undefined ? undefined : `${sheet.prefix}/body/idle/south/0`;
    }
    if (atlas === undefined || frameName === undefined) return undefined;
    if (!scene.textures.exists(atlas)) return undefined;
    const frame = scene.textures.getFrame(atlas, frameName);
    if (frame.name !== frameName) return undefined;
    // Drawn once at its size, cropped to what is not transparent (a body's
    // canvas is mostly air), then fitted to the box, nearest neighbour: a
    // thumbnail of the editor, not the world, so the scale need not be whole.
    const raw = document.createElement('canvas');
    raw.width = frame.cutWidth;
    raw.height = frame.cutHeight;
    const rawContext = raw.getContext('2d');
    if (rawContext === null) return undefined;
    rawContext.drawImage(
      frame.source.image as CanvasImageSource,
      frame.cutX,
      frame.cutY,
      frame.cutWidth,
      frame.cutHeight,
      0,
      0,
      frame.cutWidth,
      frame.cutHeight,
    );
    const box = opaqueBox(rawContext, raw.width, raw.height);
    const largest = Math.max(box.width, box.height);
    const scale = THUMB / largest;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(box.width * scale));
    canvas.height = Math.max(1, Math.round(box.height * scale));
    const context = canvas.getContext('2d');
    if (context === null) return undefined;
    context.imageSmoothingEnabled = false;
    context.drawImage(raw, box.x, box.y, box.width, box.height, 0, 0, canvas.width, canvas.height);
    return canvas;
  }
}

function keyOf(entry: PaletteEntry | undefined): string {
  return entry === undefined ? '' : `${entry.kind}:${entry.id}`;
}

/** The smallest box holding every pixel that is not fully transparent. */
function opaqueBox(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
): { x: number; y: number; width: number; height: number } {
  const alpha = context.getImageData(0, 0, width, height).data;
  let x0 = width;
  let y0 = height;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if ((alpha[(y * width + x) * 4 + 3] ?? 0) === 0) continue;
      x0 = Math.min(x0, x);
      y0 = Math.min(y0, y);
      x1 = Math.max(x1, x);
      y1 = Math.max(y1, y);
    }
  }
  if (x1 < 0) return { x: 0, y: 0, width, height };
  return { x: x0, y: y0, width: x1 - x0 + 1, height: y1 - y0 + 1 };
}
